import { useCallback, useEffect, useRef, useState } from 'react';

import {
  IGuestTokenMutationVariables,
  Platform,
  Stage,
  useGuestTokenMutation,
} from 'generated/graphql-gateway';
import { GuestToken, TokenRenewNowException } from 'remote/auth/get-current-guest-session';
import { useLogger } from 'state/logger';
import { env, getCountry, platform } from 'utils/environment';
import { RegionToISOs } from 'utils/form/constants';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { SIGN_IN_FAIL } from '../constants';
import { GuestTokenInvalid } from '../errors';
import { IGuestCredentials, IGuestDetails, IGuestSignInParams, IGuestUser } from '../types';

export const getGuestCredentials = (): IGuestCredentials | null =>
  LocalStorage.getItem(StorageKeys.GUEST);
export const clearGuestCredentials = () => LocalStorage.removeItem(StorageKeys.GUEST);
export const storeGuestCredentials = (data: IGuestCredentials) =>
  LocalStorage.setItem(StorageKeys.GUEST, data);

export const useGuestAuthentication = () => {
  const logger = useLogger();
  const [guestTokenMutation] = useGuestTokenMutation();
  const storedGuestCredentials = getGuestCredentials();
  const [signInProgress, setSignInProgress] = useState(false);

  const timeoutRef = useRef<number | null>(null);
  const tokenRenewAttempts = useRef<number>(0);

  const [guestCredentials, setGuestCredentials] = useState<IGuestCredentials | null>(
    storedGuestCredentials
  );
  let guestUser: IGuestUser | null = null;
  if (guestCredentials && !guestCredentials.anonymous) {
    guestUser = {
      email: guestCredentials.email,
      name: guestCredentials.name,
      promotionalOptIn: guestCredentials.promotionalOptIn,
      anonymous: guestCredentials.anonymous,
    };
  }
  let guestDetails: IGuestDetails | null = null;
  if (guestCredentials) {
    guestDetails = {
      orderCount: guestCredentials.details?.orderCount || 0,
    };
  }

  const authenticated = !!guestCredentials;
  const anonymousAuthenticated = !!guestCredentials && guestCredentials?.anonymous;

  const requestGuestToken = useCallback(async () => {
    const { data } = await guestTokenMutation({
      variables: {
        input: {
          country: RegionToISOs[getCountry().toUpperCase()],
          platform: platform() as any as Platform,
          stage: env() as any as Stage,
        },
      } as IGuestTokenMutationVariables,
    });
    return data;
  }, [guestTokenMutation]);

  const signIn = useCallback(
    async ({
      guest: { email, name, promotionalOptIn, anonymous },
      force = false,
    }: IGuestSignInParams) => {
      try {
        if (signInProgress) {
          return;
        }
        setSignInProgress(true);
        let token = null;
        let details: IGuestDetails | undefined = guestCredentials?.details || undefined;
        if (guestCredentials == null || force) {
          const data = await requestGuestToken();
          if (!data?.generateGuestToken) {
            throw new GuestTokenInvalid();
          }
          token = data.generateGuestToken;
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          setTokenExpiration(token);
          details = undefined; // reset guest details
        } else {
          token = guestCredentials.generateGuestToken;
        }
        storeGuestCredentials({
          email,
          name,
          promotionalOptIn,
          anonymous,
          generateGuestToken: token,
          details,
        });
        setGuestCredentials(getGuestCredentials());
      } catch (error) {
        logger.error(error);
        error.code = SIGN_IN_FAIL;
        throw error;
      } finally {
        setSignInProgress(false);
      }
    },
    [guestCredentials, requestGuestToken, logger, setGuestCredentials, signInProgress]
  );

  const signOut = useCallback(() => {
    clearGuestCredentials();
    setGuestCredentials(null);
  }, [setGuestCredentials]);

  const signInAnonymouslyInternal = useCallback(() => {
    signIn({ guest: { anonymous: true } });
  }, [signIn]);

  const signInAnonymously = useCallback(() => {
    if (guestCredentials) {
      return;
    }
    signInAnonymouslyInternal();
  }, [signInAnonymouslyInternal, guestCredentials]);

  const switchAnonymousUser = useCallback(() => {
    if (!guestCredentials) {
      // if no guest credentials, should call signInAnonymously not the switch operation
      return;
    }
    signInAnonymouslyInternal();
  }, [signInAnonymouslyInternal, guestCredentials]);

  const requestNewTokenAnonymously = useCallback(() => {
    signIn({ guest: { anonymous: true }, force: true });
  }, [signIn]);

  const updateGuestDetails = useCallback(
    (newDetails: IGuestDetails) => {
      if (!guestCredentials) {
        return;
      }
      const newGuestCredentials = { ...guestCredentials, details: newDetails };
      storeGuestCredentials(newGuestCredentials);
      guestCredentials.details = newGuestCredentials.details; // enforce sequential updates
      setGuestCredentials(getGuestCredentials());
    },
    [guestCredentials, setGuestCredentials]
  );

  const renewToken = useCallback(() => {
    if (!guestCredentials) {
      return;
    }
    signIn({ guest: guestCredentials, force: true });
  }, [guestCredentials, signIn]);

  const clearTokenExpiration = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setTokenExpiration = useCallback(
    (token: string) => {
      clearTokenExpiration();

      const guestToken = new GuestToken({ token });
      try {
        const expirationTime = guestToken.getSafestRenewalTimeout();
        timeoutRef.current = setTimeout(() => {
          renewToken();
        }, expirationTime);
      } catch (error) {
        if (error instanceof TokenRenewNowException) {
          if (tokenRenewAttempts.current <= 3) {
            tokenRenewAttempts.current += 1;
            renewToken();
          }
        } else {
          throw error;
        }
      }
    },
    [clearTokenExpiration, renewToken]
  );

  useEffect(() => {
    return () => {
      clearTokenExpiration();
    };
  }, [clearTokenExpiration]);

  useEffect(() => {
    if (guestCredentials && timeoutRef.current == null) {
      setTokenExpiration(guestCredentials.generateGuestToken);
    }
  }, [guestCredentials, setTokenExpiration]);

  return {
    guestUser,
    guestDetails,
    authenticated,
    anonymousAuthenticated,
    signIn,
    signOut,
    signInAnonymously,
    updateGuestDetails,
    switchAnonymousUser,
    requestNewTokenAnonymously,
  };
};
